/**
 * Teacher Hiring (Phase 7 / Module 1) portal-side shared types and helpers.
 *
 * Mirrors the contracts from `@bbl/validation/hiring`. The validation
 * package is the single source of truth — we re-export the inferred types
 * rather than redeclaring shapes so a server schema change shows up as a
 * typecheck error here.
 *
 * Pure helpers (stage chip colour map, stage progress derivation, next-
 * action derivation, days-in-stage maths) live here so the route files
 * stay focused on layout/state and these primitives can be unit-tested
 * without React.
 */
import {
  PIPELINE_TRANSITIONS,
  type PipelineStage,
  type InterviewSlotMode,
  type InterviewSlotStatus,
  type InterviewEvaluationDecision,
  type DemoDecision,
  type ProbationVerdict,
  type CandidateDocumentKind,
} from '@bbl/validation';

export type {
  PipelineStage,
  InterviewSlotMode,
  InterviewSlotStatus,
  InterviewEvaluationDecision,
  DemoDecision,
  ProbationVerdict,
  CandidateDocumentKind,
};

// =========================================================================
// API response shapes
// =========================================================================

export interface CandidateApplication {
  id: string;
  fullName: string;
  age: number | null;
  place: string | null;
  phoneNumber: string;
  email: string;
  qualification: string;
  experienceYears: number | null;
  maritalStatus: 'married' | 'single' | 'prefer_not_to_say' | null;
  childrenCount: number | null;
  childrenAges: number[] | null;
  coursesPreferred: string[];
  coverNote: string | null;
  referralSource: string | null;
  pipelineStage: PipelineStage;
  currentAssessmentAttemptId: string | null;
  interviewBookingId: string | null;
  userId: string | null;
  consentTerms: boolean;
  consentDataProcessing: boolean;
  submittedAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface CandidateDocument {
  id: string;
  kind: CandidateDocumentKind;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  r2Key: string;
  uploadedAt: string;
}

export interface CandidateStatusEvent {
  id: string;
  fromStage: PipelineStage | null;
  toStage: PipelineStage;
  actorUserId: string | null;
  reason: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface InterviewSlot {
  id: string;
  interviewerId: string;
  startsAt: string;
  endsAt: string;
  mode: InterviewSlotMode;
  locationOrLink: string | null;
  capacity: number;
  status: InterviewSlotStatus;
  notes: string | null;
}

export interface InterviewBooking {
  id: string;
  applicationId: string;
  slotId: string;
  status: 'booked' | 'attended' | 'cancelled' | 'no_show';
  cancelledAt: string | null;
  cancelledReason: string | null;
  attendedAt: string | null;
  createdAt: string;
}

export interface InterviewEvaluation {
  id: string;
  bookingId: string;
  overallScore: number | null;
  communicationScore: number | null;
  subjectKnowledgeScore: number | null;
  pedagogyScore: number | null;
  professionalismScore: number | null;
  notes: string | null;
  decision: InterviewEvaluationDecision;
  decidedAt: string | null;
}

export interface ApplicationBookingRow {
  id: string;
  status: 'booked' | 'attended' | 'cancelled' | 'no_show';
  cancelledAt: string | null;
  cancelledReason: string | null;
  attendedAt: string | null;
  createdAt: string;
  slot: {
    id: string;
    interviewerId: string;
    startsAt: string;
    endsAt: string;
    mode: InterviewSlotMode;
    locationOrLink: string | null;
  };
  evaluation: InterviewEvaluation | null;
}

export interface DemoEvaluationRow {
  id: string;
  sequenceNo: number;
  scheduledAt: string;
  heldAt: string | null;
  evaluatorId: string;
  topic: string | null;
  audience: string | null;
  overallScore: number | null;
  engagementScore: number | null;
  clarityScore: number | null;
  classroomManagementScore: number | null;
  mistakesNoted: string | null;
  strengthsNoted: string | null;
  notes: string | null;
  decision: DemoDecision;
  decidedAt: string | null;
}

/**
 * Promoted-teacher card embed surfaced by the application detail GET once
 * the candidate has reached `onboarded`. Optional because the backend
 * enrichment is rolling out in parallel; legacy responses may omit it.
 */
export interface PromotedTeacherEmbed {
  id: string;
  name: string;
  employeeId: string | null;
  joinedAt: string | null;
}

/**
 * Probation roll-up surfaced by the application detail GET once the
 * teacher record exists. Mirrors what the probation queue list returns.
 */
export interface ProbationSummaryEmbed {
  teacherId: string;
  weeksRecorded: number;
  latestVerdict: ProbationVerdict | null;
  latestWeekNo: number | null;
  averageScore: number | null;
}

export interface ApplicationDetailResponse {
  application: CandidateApplication;
  documents: CandidateDocument[];
  statusTimeline: CandidateStatusEvent[];
  bookings: ApplicationBookingRow[];
  demos: DemoEvaluationRow[];
  currentAssessmentAttemptId: string | null;
  /** Populated once the candidate has been promoted into the teachers table. */
  promotedTeacher?: PromotedTeacherEmbed | null;
  /** Populated alongside `promotedTeacher` once probation reviews exist. */
  probationSummary?: ProbationSummaryEmbed | null;
}

/**
 * Lightweight user reference used by the interview slot detail GET for
 * the interviewer + createdBy cards.
 */
export interface InterviewUserRef {
  id: string;
  displayName: string;
  email: string | null;
}

/**
 * Booking row enriched with the candidate name/email so the detail page
 * can deep-link straight to the application without re-fetching.
 *
 * The field is named `application` to match the `GET /interviews/slots/:id`
 * payload exactly (the handler emits `application: {...}`). It is non-null in
 * practice because that endpoint inner-joins bookings to their application,
 * but kept nullable as a defensive contract so a row with a missing join
 * renders the id-prefix fallback rather than crashing.
 */
export interface InterviewBookingWithCandidate extends InterviewBooking {
  application: {
    id: string;
    fullName: string;
    email: string;
    pipelineStage: PipelineStage;
  } | null;
}

export interface InterviewSlotDetailResponse {
  slot: InterviewSlot;
  interviewer: InterviewUserRef | null;
  createdBy: InterviewUserRef | null;
  bookings: InterviewBookingWithCandidate[];
  evaluations: InterviewEvaluation[];
}

export interface ApplicationListItem extends CandidateApplication {}

export interface ApplicationListResponse {
  items: ApplicationListItem[];
  page: number;
  pageSize: number;
  total: number;
}

/**
 * H-3: latest hiring-scope (aptitude) attempt for a booked candidate, as
 * returned by `GET /api/hiring/interviews`. `scorePercent` / `pass` are
 * nullable because an abandoned (un-finalised) attempt has no final score —
 * that case renders as a neutral "No score yet", never a red 0% fail.
 * `attemptId` is always present here: the backend only emits an `aptitude`
 * object when a finalised-or-abandoned attempt row exists (otherwise the
 * whole object is `null`).
 */
export interface InterviewCalendarBookingAptitude {
  scorePercent: number | null;
  pass: boolean | null;
  attemptId: string;
}

/**
 * H-3: candidate identity + aptitude payload the enriched calendar endpoint
 * attaches to every booking. The booking → application join is an inner join
 * server-side, so the backend always sends a `candidate` object; we keep the
 * field nullable here as a defensive contract so the calendar/list render an
 * id-prefix fallback rather than crashing if the shape ever changes.
 * `aptitude` is `null` when the application has no linked user (legacy /
 * pre-account path) or the candidate has not taken the test yet.
 */
export interface InterviewCalendarBookingCandidate {
  applicationId: string;
  fullName: string;
  email: string;
  coursesPreferred: string[];
  pipelineStage: PipelineStage;
  aptitude: InterviewCalendarBookingAptitude | null;
}

/**
 * H-3: a calendar booking row enriched with its candidate. Extends the base
 * `InterviewBooking` so it stays assignable wherever a plain booking is
 * expected (e.g. `SlotCalendar`'s booking list). Kept separate from the base
 * type because the slot-detail endpoint sends a different enrichment shape.
 */
export interface InterviewCalendarBooking extends InterviewBooking {
  candidate: InterviewCalendarBookingCandidate | null;
}

export interface InterviewCalendarResponse {
  slots: InterviewSlot[];
  bookings: InterviewCalendarBooking[];
  evaluations: InterviewEvaluation[];
}

export interface DemoEvaluatorOption {
  id: string;
  displayName: string;
  email: string;
}

export interface DemoEvaluatorsResponse {
  items: DemoEvaluatorOption[];
}

export interface ProbationTeacher {
  id: string;
  name: string;
  employeeId: string | null;
}

export interface ProbationReviewRow {
  id: string;
  teacherId: string;
  weekNo: number;
  periodStart: string;
  periodEnd: string;
  reviewerId: string | null;
  overallScore: number | null;
  improvementNotes: string | null;
  progressNotes: string | null;
  concerns: string | null;
  verdict: ProbationVerdict;
  createdAt: string;
  updatedAt: string;
  teacher?: ProbationTeacher;
}

export interface ProbationListResponse {
  items: ProbationReviewRow[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ProbationDetailTeacher {
  id: string;
  name: string;
  employeeId: string | null;
  /** Date the teacher joined the academy. Newer backends compute this from
   *  the promotion event; older callers may use `createdAt` as a fallback. */
  joinedAt?: string | null;
  createdAt?: string | null;
}

export interface ProbationDetailReviewer {
  id: string;
  displayName: string | null;
  email: string | null;
}

/** Originating teacher application (post-promotion link). Optional — only
 *  present once the extended backend projection is live. */
export interface ProbationOriginatingApplication {
  id: string;
  fullName: string;
  pipelineStage: PipelineStage;
  submittedAt: string;
}

export interface ProbationDetailResponse {
  teacher: ProbationDetailTeacher;
  reviews: ProbationReviewRow[];
  reviewers?: ProbationDetailReviewer[];
  application?: ProbationOriginatingApplication | null;
}

/** Six-card KPI strip for the probation detail header. Computed client-side
 *  from the reviews array so it stays correct regardless of whether the
 *  backend includes a `kpis` projection. */
export interface ProbationKpis {
  weeksRecorded: number;
  currentWeekNo: number | null;
  latestVerdict: ProbationVerdict | null;
  averageScore: number | null;
  onTrackRatio: number | null;
  atRiskWeeks: number;
}

export const computeProbationKpis = (
  reviews: ReadonlyArray<ProbationReviewRow>,
): ProbationKpis => {
  if (reviews.length === 0) {
    return {
      weeksRecorded: 0,
      currentWeekNo: null,
      latestVerdict: null,
      averageScore: null,
      onTrackRatio: null,
      atRiskWeeks: 0,
    };
  }
  const sortedByWeek = [...reviews].sort((a, b) => a.weekNo - b.weekNo);
  const latest = sortedByWeek[sortedByWeek.length - 1]!;
  const scored = reviews.filter(
    (r): r is ProbationReviewRow & { overallScore: number } =>
      r.overallScore !== null && Number.isFinite(r.overallScore),
  );
  const onTrack = reviews.filter((r) => r.verdict === 'on_track').length;
  const atRisk = reviews.filter((r) => r.verdict === 'at_risk').length;
  return {
    weeksRecorded: reviews.length,
    currentWeekNo: latest.weekNo,
    latestVerdict: latest.verdict,
    averageScore:
      scored.length === 0
        ? null
        : Math.round(
            (scored.reduce((sum, r) => sum + r.overallScore, 0) /
              scored.length) *
              10,
          ) / 10,
    onTrackRatio: Math.round((onTrack / reviews.length) * 100),
    atRiskWeeks: atRisk,
  };
};

export interface CandidateStatusResponse {
  application: {
    id: string;
    pipelineStage: PipelineStage;
    submittedAt: string;
    updatedAt: string;
  };
  timeline: Array<{
    id: string;
    fromStage: PipelineStage | null;
    toStage: PipelineStage;
    reason: string | null;
    createdAt: string;
  }>;
  runs: Array<{
    id: string;
    title: string;
    scope: 'hiring' | 'rules' | string;
    status: 'scheduled' | 'open' | 'closed' | 'cancelled' | string;
    opensAt: string | null;
    closesAt: string | null;
  }>;
  nextAction: string | null;
}

export interface CandidateInterviewSlot {
  id: string;
  startsAt: string;
  endsAt: string;
  mode: InterviewSlotMode;
  locationOrLink: string | null;
  capacityRemaining: number;
}

export interface CandidateInterviewSlotsResponse {
  items: CandidateInterviewSlot[];
  reason?: string;
}

// =========================================================================
// Stage labels + ordering + colour map
// =========================================================================

/**
 * Display labels for every pipeline stage. Single source of truth for
 * chip captions and timeline rows.
 */
export const STAGE_LABEL: Record<PipelineStage, string> = {
  applied: 'Applied',
  aptitude_passed: 'Aptitude passed',
  aptitude_failed: 'Aptitude failed',
  interview_booked: 'Interview booked',
  interview_passed: 'Interview passed',
  interview_failed: 'Interview failed',
  interview_waitlisted: 'Interview waitlisted',
  demo_scheduled: 'Demos scheduled',
  demo_in_progress: 'Demos in progress',
  selected: 'Selected',
  rejected: 'Rejected',
  rules_passed: 'Rules passed',
  rules_failed: 'Rules failed',
  onboarded: 'Onboarded',
  archived: 'Archived',
};

/**
 * Visual tone classes per stage. Active stages use teal/gold; failure
 * stages use muted red; terminal/closed stages use grey. The chip
 * component combines this with a stable text label so colour is never
 * the only carrier of meaning.
 */
export type StageTone = 'active' | 'success' | 'failure' | 'neutral' | 'terminal';

export const STAGE_TONE: Record<PipelineStage, StageTone> = {
  applied: 'active',
  aptitude_passed: 'success',
  aptitude_failed: 'failure',
  interview_booked: 'active',
  interview_passed: 'success',
  interview_failed: 'failure',
  interview_waitlisted: 'neutral',
  demo_scheduled: 'active',
  demo_in_progress: 'active',
  selected: 'success',
  rejected: 'failure',
  rules_passed: 'success',
  rules_failed: 'failure',
  onboarded: 'success',
  archived: 'terminal',
};

export const STAGE_TONE_CLASSES: Record<StageTone, string> = {
  active:
    'border-gold-500 bg-gold-50 text-gold-900 dark:bg-gold-700/20 dark:text-gold-50',
  success:
    'border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]',
  failure:
    'border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/10 text-[color:var(--color-danger)]',
  neutral:
    'border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]',
  terminal:
    'border-[color:var(--color-border)] bg-[color:var(--color-bg)] text-[color:var(--color-text-subtle)]',
};

/**
 * The seven visible stops on the candidate-facing stepper. The actual
 * pipeline has more states (e.g. `interview_failed`), but those are
 * outcomes of a step rather than steps themselves — the stepper folds
 * them under the stage they conclude.
 */
export const CANDIDATE_STEPS = [
  { key: 'applied', label: 'Applied' },
  { key: 'aptitude', label: 'Aptitude' },
  { key: 'interview', label: 'Interview' },
  { key: 'demos', label: 'Demos' },
  { key: 'rules', label: 'Rules' },
  { key: 'onboard', label: 'Onboard' },
  { key: 'done', label: 'Done' },
] as const;

export type CandidateStepKey = (typeof CANDIDATE_STEPS)[number]['key'];

/**
 * Map any pipeline stage onto a candidate-facing step index (0..6) and a
 * status flag — useful for rendering the seven-stop stepper plus the
 * "ended" badge when the application terminated early.
 */
export interface StepProgress {
  /** Index into `CANDIDATE_STEPS` of the currently-active step. */
  currentIndex: number;
  /** True for stages strictly before `currentIndex`. */
  completed: ReadonlyArray<CandidateStepKey>;
  /** When true, the application terminated early — render the failure badge. */
  ended: 'rejected' | 'failed' | null;
  /** Friendly headline (e.g. "Interview booked"). */
  headline: string;
}

const STEP_INDEX: Record<PipelineStage, number> = {
  applied: 0,
  aptitude_passed: 2,
  aptitude_failed: 1,
  interview_booked: 2,
  interview_passed: 3,
  interview_failed: 2,
  interview_waitlisted: 2,
  demo_scheduled: 3,
  demo_in_progress: 3,
  selected: 4,
  rejected: 0,
  rules_passed: 5,
  rules_failed: 4,
  onboarded: 6,
  archived: 6,
};

export const deriveStepProgress = (stage: PipelineStage): StepProgress => {
  const currentIndex = STEP_INDEX[stage];
  const completed = CANDIDATE_STEPS.slice(0, Math.max(0, currentIndex)).map(
    (s) => s.key,
  );
  let ended: StepProgress['ended'] = null;
  if (stage === 'rejected') ended = 'rejected';
  else if (
    stage === 'aptitude_failed' ||
    stage === 'interview_failed' ||
    stage === 'rules_failed'
  ) {
    ended = 'failed';
  }
  return {
    currentIndex,
    completed,
    ended,
    headline: STAGE_LABEL[stage],
  };
};

// =========================================================================
// State-machine helpers
// =========================================================================

/**
 * Re-exported view of the authoritative transition table from
 * `@bbl/validation/hiring`. The portal NEVER hardcodes a stage list — it
 * reads this table so a server-side change ripples to the UI on the
 * next bundle.
 */
export const PIPELINE = PIPELINE_TRANSITIONS;

/**
 * List the valid `to` stages for a given `from` stage. Returns an empty
 * array for terminal stages.
 */
export const listAllowedNextStages = (
  from: PipelineStage,
): ReadonlyArray<PipelineStage> => PIPELINE_TRANSITIONS[from];

/**
 * The action a UI element should take from the current stage. Each
 * variant is rendered by a single `<StageActionButton>` so callers don't
 * have to switch over stages themselves.
 */
export type StageAction =
  | { kind: 'issue_aptitude' }
  | { kind: 'schedule_demos' }
  | { kind: 'await_candidate_booking' }
  | { kind: 'await_candidate_attendance'; bookingId: string }
  | { kind: 'await_demo_evaluations' }
  | { kind: 'issue_rules' }
  | { kind: 'await_onboarding_accept' }
  | { kind: 'view_probation'; teacherId: string | null }
  | { kind: 'archive' }
  | { kind: 'none' };

interface NextActionInput {
  stage: PipelineStage;
  activeBookingId?: string | null;
  promotedTeacherId?: string | null;
}

/**
 * Compute the canonical HR next-action affordance for a given application
 * stage. Mirrors the spec's "What's the next action" matrix.
 */
export const computeNextAction = (input: NextActionInput): StageAction => {
  switch (input.stage) {
    case 'applied':
      return { kind: 'issue_aptitude' };
    case 'aptitude_passed':
      return { kind: 'await_candidate_booking' };
    case 'interview_booked':
      return {
        kind: 'await_candidate_attendance',
        bookingId: input.activeBookingId ?? '',
      };
    case 'interview_passed':
      return { kind: 'schedule_demos' };
    case 'demo_scheduled':
    case 'demo_in_progress':
      return { kind: 'await_demo_evaluations' };
    case 'selected':
      return { kind: 'issue_rules' };
    case 'rules_passed':
      return { kind: 'await_onboarding_accept' };
    case 'onboarded':
      return { kind: 'view_probation', teacherId: input.promotedTeacherId ?? null };
    case 'aptitude_failed':
    case 'interview_failed':
    case 'interview_waitlisted':
    case 'rules_failed':
    case 'rejected':
      return { kind: 'archive' };
    case 'archived':
      return { kind: 'none' };
    default:
      return { kind: 'none' };
  }
};

/**
 * Stages we hide from the default HR queue filter — terminal-ish states
 * that clutter the operational view.
 */
export const ACTIVE_STAGE_DEFAULTS: ReadonlyArray<PipelineStage> = [
  'applied',
  'aptitude_passed',
  'aptitude_failed',
  'interview_booked',
  'interview_passed',
  'interview_failed',
  'interview_waitlisted',
  'demo_scheduled',
  'demo_in_progress',
  'selected',
  'rules_passed',
  'rules_failed',
  'rejected',
];

export const ALL_STAGES: ReadonlyArray<PipelineStage> = Object.keys(
  PIPELINE_TRANSITIONS,
) as ReadonlyArray<PipelineStage>;

// =========================================================================
// Date math
// =========================================================================

const MS_DAY = 86_400_000;

/**
 * Whole calendar days between `iso` and now. Negative values clamp to 0.
 */
export const daysSince = (iso: string, now: Date = new Date()): number => {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 0;
  const diff = now.getTime() - then;
  if (diff <= 0) return 0;
  return Math.floor(diff / MS_DAY);
};

/**
 * Days the application has been sitting in its current stage. Looks at
 * the most recent status event matching the current stage and falls back
 * to `submittedAt` if none is present (e.g. brand-new applications).
 */
export const daysInCurrentStage = (
  app: Pick<CandidateApplication, 'pipelineStage' | 'submittedAt' | 'updatedAt'>,
  events: ReadonlyArray<Pick<CandidateStatusEvent, 'toStage' | 'createdAt'>>,
  now: Date = new Date(),
): number => {
  const matching = events
    .filter((e) => e.toStage === app.pipelineStage)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  const latest = matching[0]?.createdAt ?? app.updatedAt ?? app.submittedAt;
  return daysSince(latest, now);
};

/**
 * Bytes → friendly string (KB / MB). Used by the document list.
 */
export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// =========================================================================
// Document upload helpers
// =========================================================================

export const DOCUMENT_KIND_LABEL: Record<CandidateDocumentKind, string> = {
  certificate: 'Certificate',
  id_proof: 'ID proof',
  resume: 'Resume / CV',
  transcript: 'Transcript',
  other: 'Other',
};

export const DOCUMENT_KIND_OPTIONS: ReadonlyArray<{
  value: CandidateDocumentKind;
  label: string;
}> = (
  Object.keys(DOCUMENT_KIND_LABEL) as CandidateDocumentKind[]
).map((k) => ({ value: k, label: DOCUMENT_KIND_LABEL[k] }));

// =========================================================================
// Verdict / decision labels
// =========================================================================

export const PROBATION_VERDICT_LABEL: Record<ProbationVerdict, string> = {
  pending: 'Pending',
  on_track: 'On track',
  needs_improvement: 'Needs improvement',
  at_risk: 'At risk',
  terminate: 'Terminate',
};

export const PROBATION_VERDICT_TONE: Record<ProbationVerdict, StageTone> = {
  pending: 'neutral',
  on_track: 'success',
  needs_improvement: 'active',
  at_risk: 'failure',
  terminate: 'failure',
};

export const DEMO_DECISION_LABEL: Record<DemoDecision, string> = {
  pending: 'Pending',
  passed: 'Passed',
  failed: 'Failed',
};

export const INTERVIEW_DECISION_LABEL: Record<
  InterviewEvaluationDecision,
  string
> = {
  pending: 'Pending',
  passed: 'Passed',
  failed: 'Failed',
  waitlisted: 'Waitlisted',
};

export const SLOT_MODE_LABEL: Record<InterviewSlotMode, string> = {
  online: 'Online',
  in_person: 'In person',
};

export const SLOT_STATUS_LABEL: Record<InterviewSlotStatus, string> = {
  open: 'Open',
  full: 'Full',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

export const SLOT_STATUS_TONE: Record<InterviewSlotStatus, StageTone> = {
  open: 'success',
  full: 'active',
  cancelled: 'failure',
  completed: 'terminal',
};
